import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RefreshCw, Activity, CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import { useBotMergeMonitoring, SignalEvent } from '@/hooks/useBotMergeMonitoring';
import { format } from 'date-fns';

export const BotMergeSignalMonitor: React.FC = () => {
  const [timeframe, setTimeframe] = useState<'today' | '7d' | '30d'>('today');
  const { signalEvents, stats, isLoading, refetch } = useBotMergeMonitoring(timeframe);

  const getStatusBadge = (success: boolean) => {
    if (success) {
      return <Badge variant="secondary" className="bg-green-500/10 text-green-500 border-green-500/20">Success</Badge>;
    } else {
      return <Badge variant="destructive">Failed</Badge>;
    }
  };

  const getTimeframeName = (tf: string) => {
    switch (tf) {
      case 'today': return 'Today';
      case '7d': return 'Last 7 Days';
      case '30d': return 'Last 30 Days';
      default: return 'Today';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Signal Monitor</h2>
          <p className="text-muted-foreground">Real-time monitoring of bot signal forwards</p>
        </div>
        <Button onClick={refetch} disabled={isLoading} size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Time Filter */}
      <Tabs value={timeframe} onValueChange={(value) => setTimeframe(value as any)}>
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="7d">Last 7 Days</TabsTrigger>
          <TabsTrigger value="30d">Last 30 Days</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signals</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSignals}</div>
            <p className="text-xs text-muted-foreground">{getTimeframeName(timeframe)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{stats.successRate}%</div>
            <p className="text-xs text-muted-foreground">
              {stats.successfulForwards}/{stats.successfulForwards + stats.failedForwards} forwards
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{stats.successfulForwards}</div>
            <p className="text-xs text-muted-foreground">Forwards completed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{stats.failedForwards}</div>
            <p className="text-xs text-muted-foreground">Forward errors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.averageResponseTime}ms</div>
            <p className="text-xs text-muted-foreground">Response time</p>
          </CardContent>
        </Card>
      </div>

      {/* Real-time Signal Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Real-time Signal Feed
            {isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {signalEvents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No signals found for {getTimeframeName(timeframe).toLowerCase()}
              </div>
            ) : (
              signalEvents.map((event) => (
                <div key={event.id} className="border rounded-lg p-4 space-y-3">
                  {/* Signal Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{event.action}</Badge>
                      <span className="font-medium">{event.instrument}</span>
                      <span className="text-sm text-muted-foreground">
                        {event.bot?.[0]?.name || 'Unknown Bot'}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {format(new Date(event.created_at), 'HH:mm:ss')}
                    </div>
                  </div>

                  {/* Forward Results */}
                  {event.forward_logs.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-muted-foreground">Forwards:</h4>
                      <div className="grid gap-2">
                        {event.forward_logs.map((log) => (
                          <div key={log.id} className="flex items-center justify-between bg-muted/50 rounded p-2">
                             <div className="flex items-center gap-2">
                               {getStatusBadge(log.success)}
                               <span className="text-sm">
                                 {log.endpoint?.[0]?.name || 'Unknown Endpoint'} - {log.endpoint?.[0]?.bot?.[0]?.name || 'Unknown Parent'}
                               </span>
                               {log.duration_ms && (
                                 <span className="text-xs text-muted-foreground">
                                   {log.duration_ms}ms
                                 </span>
                               )}
                            </div>
                            {log.error_message && (
                              <span className="text-xs text-red-500 max-w-xs truncate">
                                {log.error_message}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Forwards */}
                  {event.forward_logs.length === 0 && (
                    <div className="text-sm text-muted-foreground italic">
                      No forwards found for this signal
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};